import { FieldType } from '../enums/field-type.enum';
import { TaxonomyStatus } from '../enums/taxonomy-status.enum';
import { IQueryService } from '../interfaces/query.service';
import { Cache } from '../models/excel/cache/tables/cache.model';
import { CascadingTable } from '../models/excel/cache/tables/cascading-table.model';
import { ChildDropDownItemsTable } from '../models/excel/cache/tables/child-dropdown-items-table.model';
import { ChildMdmDropDownItemsTable } from '../models/excel/cache/tables/child-mdm-dropdown-child-table.model';
import { DropDownItemsTable } from '../models/excel/cache/tables/dropdown-items-table.model';
import { FieldsTable } from '../models/excel/cache/tables/fields-table.model';
import { MdmDropDownItemsTable } from '../models/excel/cache/tables/mdm-dropdown-items-table.model';
import { MdmSearchDropDownItemsTable } from '../models/excel/cache/tables/mdm-search-dropdown-items-table.model';
import { TaxonomiesTable } from '../models/excel/cache/tables/taxonomies-table.model';
import { TaxonomyValuesTable } from '../models/excel/cache/tables/taxonomy-values-table.model';
import { DataCell } from '../models/excel/cells/data-cell.model';
import { HeaderCell } from '../models/excel/cells/header-cell.model';
import { Field } from '../models/excel/entities/field.model';
import { Range } from '../models/excel/entities/range.model';
import { TaxonomyCell } from '../models/excel/taxonomies/taxonomy-cell.model';
import { TaxonomyRow } from '../models/excel/taxonomies/taxonomy-row.mode';
import { AppHelper } from './../helpers/app.helper';
import { TaxonomyValue } from './../models/excel/taxonomies/taxonomy-value.model';
import { ChildDropDownItem } from './../models/fields/child-dropdown-item.model';
import { DropDownItem } from './../models/fields/dropdown-item.model';
import { LoggerService } from './logger.service';


export class ExcelService {
    //#region Fields

    private static readonly dataSheetName = 'TB_DATA';
    private static readonly dropDownItemsSheetName = 'TB_DDI';
    private static readonly childDropDownItemsSheetName = 'TB_CDDI';
    private static readonly mdmDropDownItemsSheetName = 'TB_MDM_DDI';
    private static readonly childMdmDropDownItemsSheetName = 'TB_MDM_CDDI';
    private static readonly mdmSearchDropDownItemsSheetName = 'TB_MDM_SDDI';
    private static readonly taxonomyValuesSheetName = 'TB_TAX_DDI';
    private static readonly cascadingSheetName = 'TB_CSC';
    private static readonly taxonomiesTableName = 'TB_Taxonomies';
    private static readonly fieldsTableName = 'TB_Fields';
    private static readonly dropDownItemsTableName = 'TB_Items';
    private static readonly childDropDownItemsTableName = 'TB_Child_Items';
    private static readonly mdmDropDownItemsTableName = 'TB_Mdm_Items';
    private static readonly childMdmDropDownItemsTableName = 'TB_Child_Mdm_Items';
    private static readonly mdmSearchDropDownItemsTableName = 'TB_Mdm_Search_Items';
    private static readonly taxonomyValuesTableName = 'TB_Taxonomy_Items';
    private static readonly cascadingTableName = 'TB_Cascading';

    private static readonly noItemsMessage = 'There are no drop-down items.';
    private static readonly enterValueMessage = 'Enter a value to search for MDM items.';
    private static readonly noItemsFoundMessage = 'There are no MDM items matching your search criteria.';
    private static readonly staticValueMessage = 'The value is static.';

    private static readonly defaultColumnName = '#';
    private static readonly defaultItemDelimiter = '|';
    private static readonly defaultListItemDelimiter = ',';

    private static readonly numberValidationFrom = -79228162514264337593543950335; // C# decimal.MinValue.
    private static readonly dateValidationFrom = '2001-01-01';
    private static readonly dateValidationTo = '9999-12-31';
    private static readonly batchSize = 20;
    private static readonly metadataBatchSize = 200;

    //private static readonly maxDirectDropDownListLength = 8000;
    private static readonly columnWidthCoefficient = 6.5; // Coefficient to work properly
    private static readonly minColumnWidth = ExcelService.columnWidthCoefficient * 10; // 10 symbols

    private cache: Cache;

    //#endregion

    //#region C-tor

    constructor(
        private queryService: IQueryService,
        private appHelper: AppHelper,
        private logger: LoggerService) { }

    //#endregion

    //#region Initialization

    async initializeAsync(context: Excel.RequestContext, taxonomyIds: number[]): Promise<void> {
        this.logger.info('Initialization...');

        let dataSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.dataSheetName);
        let dropDownItemsSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.dropDownItemsSheetName);
        let childDropDownItemsSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.childDropDownItemsSheetName);
        let mdmDropDownItemsSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.mdmDropDownItemsSheetName);
        let childMdmDropDownItemsSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.childMdmDropDownItemsSheetName);
        let mdmSearchDropDownItemsSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.mdmSearchDropDownItemsSheetName);
        let taxonomyValuesSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.taxonomyValuesSheetName);
        let cascadingSheet = context.workbook.worksheets.getItemOrNullObject(ExcelService.cascadingSheetName);

        let taxonomiesTable = dataSheet.tables.getItemOrNullObject(ExcelService.taxonomiesTableName);
        let fieldsTable = dataSheet.tables.getItemOrNullObject(ExcelService.fieldsTableName);
        let dropDownItemsTable = dropDownItemsSheet.tables.getItemOrNullObject(ExcelService.dropDownItemsTableName);
        let childDropDownItemsTable = childDropDownItemsSheet.tables.getItemOrNullObject(ExcelService.childDropDownItemsTableName);
        let mdmDropDownItemsTable = mdmDropDownItemsSheet.tables.getItemOrNullObject(ExcelService.mdmDropDownItemsTableName);
        let childMdmDropDownItemsTable = childMdmDropDownItemsSheet.tables.getItemOrNullObject(ExcelService.childMdmDropDownItemsTableName);
        let mdmSearchDropDownItemsTable = mdmSearchDropDownItemsSheet.tables.getItemOrNullObject(ExcelService.mdmSearchDropDownItemsTableName);
        let taxonomyValuesTable = taxonomyValuesSheet.tables.getItemOrNullObject(ExcelService.taxonomyValuesTableName);
        let cascadingTable = cascadingSheet.tables.getItemOrNullObject(ExcelService.cascadingTableName);

        await context.sync();

        if (dataSheet.isNullObject) {
            dataSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.dataSheetName);
        }

        if (dropDownItemsSheet.isNullObject) {
            dropDownItemsSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.dropDownItemsSheetName);
        }

        if (childDropDownItemsSheet.isNullObject) {
            childDropDownItemsSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.childDropDownItemsSheetName);
        }

        if (mdmDropDownItemsSheet.isNullObject) {
            mdmDropDownItemsSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.mdmDropDownItemsSheetName);
        }

        if (childMdmDropDownItemsSheet.isNullObject) {
            childMdmDropDownItemsSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.childMdmDropDownItemsSheetName);
        }

        if (mdmSearchDropDownItemsSheet.isNullObject) {
            mdmSearchDropDownItemsSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.mdmSearchDropDownItemsSheetName);
        }

        if (taxonomyValuesSheet.isNullObject) {
            taxonomyValuesSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.taxonomyValuesSheetName);
        }

        if (cascadingSheet.isNullObject) {
            cascadingSheet = this.addSystemSheet(
                context.workbook,
                ExcelService.cascadingSheetName);
        }

        if (taxonomiesTable.isNullObject) {
            taxonomiesTable = this.addSystemTable(
                dataSheet,
                ExcelService.taxonomiesTableName,
                'B1:C2',
                ['TaxonomyId', 'FieldIds']
            );
        }

        if (fieldsTable.isNullObject) {
            fieldsTable = this.addSystemTable(
                dataSheet,
                ExcelService.fieldsTableName,
                'E1:P2',
                ['FieldId', 'TaxonomyId', 'FieldType', 'Name', 'Hidden', 'Mandatory', 'Limit', 'Delimiter', 'DateFormat', 'MdmEntity', 'StaticValue', 'DisplayCode']
            );
        }

        if (dropDownItemsTable.isNullObject) {
            dropDownItemsTable = this.addSystemTable(
                dropDownItemsSheet,
                ExcelService.dropDownItemsTableName,
                'B1:B2',
                [ExcelService.defaultColumnName]
            );
        }

        if (childDropDownItemsTable.isNullObject) {
            childDropDownItemsTable = this.addSystemTable(
                childDropDownItemsSheet,
                ExcelService.childDropDownItemsTableName,
                'B1:D2',
                ['FieldId', 'ItemId', 'ItemsCount']
            );
        }

        if (mdmDropDownItemsTable.isNullObject) {
            mdmDropDownItemsTable = this.addSystemTable(
                mdmDropDownItemsSheet,
                ExcelService.mdmDropDownItemsTableName,
                'B1:B2',
                [ExcelService.defaultColumnName]
            );
        }

        if (childMdmDropDownItemsTable.isNullObject) {
            childMdmDropDownItemsTable = this.addSystemTable(
                childMdmDropDownItemsSheet,
                ExcelService.childMdmDropDownItemsTableName,
                'B1:E2',
                ['TaxonomyId', 'FieldId', 'ItemId', 'ItemsCount']
            );
        }

        if (mdmSearchDropDownItemsTable.isNullObject) {
            mdmSearchDropDownItemsTable = this.addSystemTable(
                mdmSearchDropDownItemsSheet,
                ExcelService.mdmSearchDropDownItemsTableName,
                'B1:E2',
                ['TaxonomyId', 'FieldId', 'ItemId', 'ItemsCount']
            );
        }

        if (taxonomyValuesTable.isNullObject) {
            taxonomyValuesTable = this.addSystemTable(
                taxonomyValuesSheet,
                ExcelService.taxonomyValuesTableName,
                'B1:B2',
                [ExcelService.defaultColumnName]
            );
        }

        if (cascadingTable.isNullObject) {
            cascadingTable = this.addSystemTable(
                cascadingSheet,
                ExcelService.cascadingTableName,
                'B1:D2',
                ['TaxonomyId', 'FieldId', 'ItemsCount']
            );
        }

        await context.sync();

        this.logger.debug('Loading cached data..');

        const taxonomiesRange = taxonomiesTable.getRange();
        taxonomiesRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const fieldsRange = fieldsTable.getRange();
        fieldsRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const dropDownItemsRange = dropDownItemsTable.getRange();
        dropDownItemsRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const childDropDownItemsRange = childDropDownItemsTable.getRange();
        childDropDownItemsRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const mdmDropDownItemsRange = mdmDropDownItemsTable.getRange();
        mdmDropDownItemsRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const childMdmDropDownItemsRange = childMdmDropDownItemsTable.getRange();
        childMdmDropDownItemsRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const mdmSearchDropDownItemsRange = mdmSearchDropDownItemsTable.getRange();
        mdmSearchDropDownItemsRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const taxonomyValuesRange = taxonomyValuesTable.getRange();
        taxonomyValuesRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const cascadingRange = cascadingTable.getRange();
        cascadingRange.load(['columnIndex', 'rowCount', 'columnCount', 'values']);
        await context.sync();

        const cache = new Cache(
            new TaxonomiesTable(new Range(0, taxonomiesRange.columnIndex, 2, taxonomiesRange.columnCount, ExcelService.dataSheetName)),
            new FieldsTable(new Range(0, fieldsRange.columnIndex, 2, fieldsRange.columnCount, ExcelService.dataSheetName)),
            new DropDownItemsTable(new Range(0, dropDownItemsRange.columnIndex, dropDownItemsRange.rowCount, 1, ExcelService.dropDownItemsSheetName)),
            new ChildDropDownItemsTable(3, new Range(0, childDropDownItemsRange.columnIndex, 2, childDropDownItemsRange.columnCount, ExcelService.childDropDownItemsSheetName)),
            new MdmDropDownItemsTable(new Range(0, mdmDropDownItemsRange.columnIndex, mdmDropDownItemsRange.rowCount, 1, ExcelService.mdmDropDownItemsSheetName)),
            new ChildMdmDropDownItemsTable(4, new Range(0, childMdmDropDownItemsRange.columnIndex, 2, childMdmDropDownItemsRange.columnCount, ExcelService.childMdmDropDownItemsSheetName)),
            new MdmSearchDropDownItemsTable(this.appHelper, 4, new Range(0, mdmSearchDropDownItemsRange.columnIndex, 2, mdmSearchDropDownItemsRange.columnCount, ExcelService.mdmSearchDropDownItemsSheetName)),
            new TaxonomyValuesTable(new Range(0, taxonomyValuesRange.columnIndex, taxonomyValuesRange.rowCount, 1, ExcelService.taxonomyValuesSheetName)),
            new CascadingTable(this.appHelper, 3, new Range(0, cascadingRange.columnIndex, 2, cascadingRange.columnCount, ExcelService.cascadingSheetName)));

        const taxonomyIdsToRemove: number[] = [];

        if (taxonomiesRange.values[1][0] !== '') { // if contains data
            // Process taxonomies
            this.logger.debug(`Processing taxonomies.. Count: '${taxonomiesRange.rowCount - 1}'.`);

            for (let r = 1 /* 1: header */; r < taxonomiesRange.rowCount; r++) {
                const taxonomyValues = taxonomiesRange.values[r];
                const taxonomyId = +taxonomyValues[0];
                const taxonomyFieldIds = taxonomyValues[1]
                    .toString()
                    .split(ExcelService.defaultItemDelimiter)
                    .map(v => +v);

                cache.taxonomies.add(taxonomyId, taxonomyFieldIds);

                if (taxonomyIds.indexOf(taxonomyId) === -1) {
                    taxonomyIdsToRemove.push(taxonomyId);
                }
            }

            // Process fields
            this.logger.debug(`Processing fields.. Count: '${fieldsRange.rowCount - 1}'.`);

            for (let r = 1 /* 1: header */; r < fieldsRange.rowCount; r++) {
                const fieldValues = fieldsRange.values[r];
                const field = new Field(
                    +fieldValues[0],
                    +fieldValues[1],
                    +fieldValues[2],
                    fieldValues[3],
                    +fieldValues[4] === 1,
                    +fieldValues[5] === 1,
                    +fieldValues[6],
                    fieldValues[7],
                    fieldValues[8],
                    fieldValues[9],
                    fieldValues[10],
                    fieldValues[11]);

                cache.fields.add(field);
            }

            // Process drop-down items
            if (dropDownItemsRange.values[0][0] !== ExcelService.defaultColumnName) { // if contains data
                this.logger.debug(`Processing drop-down fields.. Count: '${dropDownItemsRange.columnCount}'.`);

                for (let c = 0; c < dropDownItemsRange.columnCount; c++) {
                    const fieldId = +dropDownItemsRange.values[0][c];
                    const itemsCount = +dropDownItemsRange.values[1][c];
                    const dropdownItems: DropDownItem[] = [];

                    // 2: header + items count
                    for (let i = 0, id = 2, code = itemsCount + 2, name = itemsCount * 2 + 2; i < itemsCount; i++, id++, code++, name++) {
                        dropdownItems.push(new DropDownItem(
                            dropDownItemsRange.values[id][c],
                            undefined,
                            dropDownItemsRange.values[name][c],
                            dropDownItemsRange.values[code][c]));
                    }

                    cache.dropDownItems.add(fieldId, dropdownItems);
                }
            }

            // Process child drop-down items
            if (childDropDownItemsRange.values[1][0] !== '') { // if contains data
                this.logger.debug(`Processing child drop-down fields.. Count: '${childDropDownItemsRange.rowCount - 1}'.`);

                for (let r = 1 /* 1: header */; r < childDropDownItemsRange.rowCount; r++) {
                    const itemValues = childDropDownItemsRange.values[r];
                    const fieldId = +itemValues[0];
                    const itemId = itemValues[1];
                    const itemsCount = +itemValues[2];
                    const childItems: ChildDropDownItem[] = [];

                    // 3: FieldId + ItemId + ItemsCount
                    for (let i = 0, c = 3; i < itemsCount; i++, c++) {
                        const childPair = itemValues[c].split(ExcelService.defaultItemDelimiter);
                        const childFieldId = +childPair[0];
                        const childItemId = childPair[1];

                        childItems.push(new ChildDropDownItem(childFieldId, childItemId));
                    }

                    cache.childDropDownItems.add(fieldId, itemId, childItems);
                }
            }

            // Process mdm drop-down items
            if (mdmDropDownItemsRange.values[0][0] !== ExcelService.defaultColumnName) { // if contains data
                this.logger.debug(`Processing MDM drop-down fields.. Count: '${mdmDropDownItemsRange.columnCount}'.`);

                for (let c = 0; c < mdmDropDownItemsRange.columnCount; c++) {
                    const keyPair = mdmDropDownItemsRange.values[0][c].split(ExcelService.defaultItemDelimiter);
                    const fieldId = +keyPair[0];
                    const taxonomyId = +keyPair[1];
                    const itemsCount = +mdmDropDownItemsRange.values[1][c];
                    const dropdownItems: DropDownItem[] = [];

                    // 2: header + items count
                    for (let i = 0, id = 2, code = itemsCount + 2, name = itemsCount * 2 + 2; i < itemsCount; i++, id++, code++, name++) {
                        dropdownItems.push(new DropDownItem(
                            mdmDropDownItemsRange.values[id][c],
                            undefined,
                            mdmDropDownItemsRange.values[name][c],
                            mdmDropDownItemsRange.values[code][c]));
                    }

                    cache.mdmDropDownItems.add(taxonomyId, fieldId, dropdownItems);
                }
            }

            // Process child mdm drop-down items
            if (childMdmDropDownItemsRange.values[1][0] !== '') { // if contains data
                this.logger.debug(`Processing child MDM drop-down fields.. Count: '${childMdmDropDownItemsRange.rowCount - 1}'.`);

                for (let r = 1 /* 1: header */; r < childMdmDropDownItemsRange.rowCount; r++) {
                    const mdmItemValues = childMdmDropDownItemsRange.values[r];
                    const taxonomyId = +mdmItemValues[0];
                    const fieldId = +mdmItemValues[1];
                    const itemId = mdmItemValues[2];
                    const itemsCount = +mdmItemValues[3];
                    const childItems: ChildDropDownItem[] = [];

                    // 4: FieldId + TaxonomyId + ItemId + ItemsCount
                    for (let i = 0, c = 4; i < itemsCount; i++, c++) {
                        const childPair = mdmItemValues[c].split(ExcelService.defaultItemDelimiter);
                        const childFieldId = +childPair[0];
                        const childItemId = childPair[1];

                        childItems.push(new ChildDropDownItem(childFieldId, childItemId));
                    }

                    cache.childMdmDropDownItems.add(taxonomyId, fieldId, itemId, childItems);
                }
            }

            // Process mdm search drop-down items
            if (mdmSearchDropDownItemsRange.values[1][0] !== '') { // if contains data
                this.logger.debug(`Processing MDM SEARCH drop-down fields.. Count: '${mdmSearchDropDownItemsRange.rowCount - 1}'.`);

                for (let r = 1 /* 1: header */; r < mdmSearchDropDownItemsRange.rowCount; r++) {
                    const mdmSearchItemValues = mdmSearchDropDownItemsRange.values[r];
                    const taxonomyId = +mdmSearchItemValues[0];
                    const fieldId = +mdmSearchItemValues[1];
                    const itemId = mdmSearchItemValues[2];
                    const itemsCount = +mdmSearchItemValues[3];
                    const items: DropDownItem[] = mdmSearchItemValues
                        .slice(4, itemsCount + 4) // 4: FieldId + TaxonomyId + ItemId + ItemsCount
                        .map(i => new DropDownItem(undefined, undefined, i, i));

                    cache.mdmSearchDropDownItems.add(taxonomyId, fieldId, itemId, items);
                }
            }

            // Process taxonomy values
            if (taxonomyValuesRange.values[0][0] !== ExcelService.defaultColumnName) { // if contains data
                this.logger.debug(`Processing TwT drop-down fields.. Count: '${taxonomyValuesRange.columnCount}'.`);

                for (let c = 0; c < taxonomyValuesRange.columnCount; c++) {
                    const taxonomyId = +taxonomyValuesRange.values[0][c];
                    const itemsCount = +taxonomyValuesRange.values[1][c];
                    const dropdownItems: DropDownItem[] = [];

                    for (let i = 0, code = 2, name = itemsCount + 2; i < itemsCount; i++, code++, name++) {
                        dropdownItems.push(new DropDownItem(
                            undefined,
                            undefined,
                            taxonomyValuesRange.values[name][c],
                            taxonomyValuesRange.values[code][c]));
                    }

                    cache.taxonomyValues.add(taxonomyId, dropdownItems);
                }
            }

            // Process cascading
            if (cascadingRange.values[1][0] !== '') { // if contains data
                this.logger.debug(`Processing cascading entries.. Count: '${cascadingRange.rowCount - 1}'.`);

                for (let r = 1 /* 1: header */; r < cascadingRange.rowCount; r++) {
                    const cellValues = cascadingRange.values[r];
                    const taxonomyId = +cellValues[0];
                    const fieldId = +cellValues[1];
                    const itemsCount = +cellValues[2];
                    const items: string[] = cellValues.slice(3, itemsCount + 3); // 3: TaxonomyId + FieldId + ItemsCount

                    cache.cascading.add(taxonomyId, fieldId, items);
                }
            }
        }

        this.cache = cache;

        for (const taxonomyId of taxonomyIdsToRemove) {
            await this.onTableRemoved(context.workbook, taxonomyId);
        }
    }

    //#endregion

    //#region Create a table

    createTable(range: Excel.Range, name: string, taxonomyId: number, fieldIds: number[]): Excel.Table {
        this.logger.debug(`Creating a table.. Name: '${name}'. Fields count: '${fieldIds.length}'.`);

        range.clear(Excel.ClearApplyTo.all);
        range.dataValidation.clear();
        range.format.autofitRows();

        const table = range.worksheet.tables.add(range, true);
        table.name = name;
        table.showFilterButton = false;

        this.addTaxonomy(range.context.workbook, taxonomyId, fieldIds);

        return table;
    }

    setupHeaders(table: Excel.Table, cells: HeaderCell[]): void {
        this.logger.debug('Setting up headers..');

        for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];
            const range = table.columns.getItemAt(i).getHeaderRowRange();

            this.setHeaderAligning(range);

            range.values = [[cell.value]];

            const headerWidth = cell.value.length * ExcelService.columnWidthCoefficient;
            const dataWidth = cell.width ? cell.width * ExcelService.columnWidthCoefficient : 0;

            range.format.columnWidth = Math.max(headerWidth, dataWidth, ExcelService.minColumnWidth);

            let message = cell.fieldId
                ? '[' + cell.fieldId + ']'
                : '';

            if (cell.description) {
                message += message
                    ? ' ' + cell.description
                    : cell.description;
            }

            if (message) {
                range.dataValidation.prompt = {
                    showPrompt: true,
                    title: 'Description',
                    message: message,
                };
            }
        }
    }

async setupData(table: Excel.Table, taxonomyId: number, cells: DataCell[], rowCount: number): Promise<void> {
    this.logger.debug('--- [DEBUG START] setupData ---');
    this.logger.debug(`Total Columns: ${cells.length}, Total Rows: ${rowCount}`);

    const workbook = table.context.workbook;
    const batchSize = Math.max(1, Math.floor(rowCount / 10));
    const maxRowsPerSync = Math.max(100, batchSize);

    // 1. Get Table Metadata (Coordinates)
    const headerRowRange = table.getHeaderRowRange();
    headerRowRange.load(['rowIndex', 'columnIndex']);
    await table.context.sync();

    const headerRowIndex = headerRowRange.rowIndex;
    const headerColumnIndex = headerRowRange.columnIndex;

    for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        const fieldName = cell.field.name || 'Unnamed';
        this.logger.debug(`[Col ${i}]  Processing: "${fieldName}" (Type: ${cell.field.type})`);

        
        const range = table.columns.getItemAt(i).getDataBodyRange();

        // Ensure field exists in cache
        if (cell.field.id && !this.cache.fields.exists(cell.field.id)) {
            this.addField(workbook, cell.field);
        }

        this.setDataAligning(range);

        // Targeted range for formatting/validation
        const limitedRowCount = Math.min(rowCount, maxRowsPerSync);
        const firstDataCell = table.worksheet.getRangeByIndexes(headerRowIndex + 1, headerColumnIndex + i, limitedRowCount, 1);

        try {
            switch (cell.field.type) {
                case FieldType.Text:
                    this.logger.debug(`[Col ${i}] Setting Text Format.`);
                    this.setTextDataFormat(firstDataCell, limitedRowCount, 1);
                    if (cell.field.characterLimit) {
                        this.logger.debug(`[Col ${i}] Applying Char Limit: ${cell.field.characterLimit}`);
                        this.setTextDataValidation(firstDataCell, cell.field.characterLimit);
                    }
                    break;

                case FieldType.Number:
                    this.logger.debug(`[Col ${i}] Setting Number Format & Validation.`);
                    this.setNumberDataFormat(firstDataCell, limitedRowCount, 1);
                    this.setNumberDataValidation(firstDataCell);
                    break;

                case FieldType.Date:
                    this.logger.debug(`[Col ## ${i}] Setting Date Format: ${cell.field.dateFormat}`);
                    this.setDataFormat(firstDataCell, limitedRowCount, 1, cell.field.dateFormat);
                    this.setDateDataValidation(firstDataCell);
                    break;

                case FieldType.Dropdown:
                case FieldType.MDMData:
                case FieldType.Taxonomy:
                    this.logger.debug(`[Col ${i}] Processing Complex Metadata (Items: ${cell.dropDownItems?.length}).`);
                    await this.ensureDropdownCacheWithLogging(workbook, taxonomyId, cell, i);                                   
                    
                    this.setTextDataFormat(firstDataCell, limitedRowCount, 1);
                    const vRange = this.getValidationRangeForType(workbook, taxonomyId, cell);
                    if (vRange) {
                        this.setDropDownDataValidation(firstDataCell, vRange);
                    } else {
                        this.setEmptyDropDownDataValidation(firstDataCell);
                    }
                    break;

                case FieldType.MDMSearch:
                    this.logger.debug(`[Col ${i}] Setting MDM Search Validation.`);
                    this.setTextDataFormat(firstDataCell, limitedRowCount, 1);
                    this.setMessageDataValidation(firstDataCell, ExcelService.enterValueMessage);
                    break;

                case FieldType.Static:
                    this.logger.debug(`[Col ${i}] Setting Static Value.`);
                    this.setTextDataFormat(firstDataCell, limitedRowCount, 1);
                    if (cell.field.staticValue) {
                        this.setValue(firstDataCell, limitedRowCount, 1, cell.field.staticValue);
                    }
                    this.setMessageDataValidation(firstDataCell, ExcelService.staticValueMessage);
                    break;

                default:
                    this.logger.warn(`[Col ${i}] Unknown field type: ${cell.field.type}`);
                    throw new Error(`Unknown field type: ${cell.field.type}.`);
            }

            // 2. CRITICAL SYNC: Empty the "bucket" after every single column
            this.logger.debug(`[Col ${i}] Finalizing column sync...`);
            await table.context.sync();
            this.logger.debug(`[Col ${i}] Success.`);

        } catch (error) {
            this.logger.error(`[Col ${i}] FAILED on field "${fieldName}".`);
            if (error.code === "RequestPayloadSizeLimitExceeded") {
                this.logger.error("Payload too large! Consider reducing the number of dropdown items or rows.");
            }
            throw error; 
        }
    }
    this.logger.debug('--- [DEBUG END] setupData Completed ---');
}

/**
 * Micro-Batches the metadata items to prevent the "Request Aborted" error
 */
private async ensureDropdownCacheWithLogging(workbook: Excel.Workbook, taxonomyId: number, cell: DataCell, colIdx: number): Promise<void> {
    const fieldId = cell.field.id;
    const items = cell.dropDownItems || [];

    // Check if the parent items are already cached in the workbook
    if (this.isAlreadyCached(taxonomyId, cell)) {
        this.logger.debug(`[Col ${colIdx}] Items already in cache. Skipping metadata write.`);
        return;
    }

    this.logger.debug(`[Col ${colIdx}] Writing metadata for ${items.length} items...`);
    
    // Process parent items
    this.addMetadataByType(workbook, taxonomyId, cell);

    // Process children with a sync every 50 items to flush the payload buffer
    for (let j = 0; j < items.length; j++) {
        const item = items[j];
        if (item.childItems && item.childItems.length > 0) {
            this.addChildMetadataByType(workbook, taxonomyId, cell, item);
        }

        /// Empty the bucket every N items to prevent timeout
        if (j > 0 && j % ExcelService.metadataBatchSize === 0) {
            this.logger.debug(`[Col ${colIdx}] Micro-syncing metadata batch (processed ${j} items)...`);
            await workbook.context.sync();
        }
    }
    
    this.logger.debug(`[Col ${colIdx}] Metadata write complete. Finalizing sync...`);
    await workbook.context.sync();
}

/**
 * Routing helpers to keep the main loop clean
 */
private isAlreadyCached(taxonomyId: number, cell: DataCell): boolean {
    if (cell.field.type === FieldType.Dropdown) return this.cache.dropDownItems.exists(cell.field.id);
    if (cell.field.type === FieldType.MDMData) return this.cache.mdmDropDownItems.exists(taxonomyId, cell.field.id);
    if (cell.field.type === FieldType.Taxonomy) return this.cache.taxonomyValues.exists(cell.field.taxonomyId);
    return false;
}

private addMetadataByType(workbook: Excel.Workbook, taxonomyId: number, cell: DataCell) {
    if (cell.field.type === FieldType.Dropdown) this.addDropDownItems(workbook, cell.field.id, cell.dropDownItems);
    if (cell.field.type === FieldType.MDMData) this.addMdmDropDownItems(workbook, taxonomyId, cell.field.id, cell.dropDownItems);
    if (cell.field.type === FieldType.Taxonomy) this.addTaxonomyValues(workbook, cell.field.taxonomyId, cell.dropDownItems);
}

private addChildMetadataByType(workbook: Excel.Workbook, taxonomyId: number, cell: DataCell, item: any) {
    if (cell.field.type === FieldType.Dropdown) this.addChildDropDownItems(workbook, cell.field.id, item.id, item.childItems);
    if (cell.field.type === FieldType.MDMData) this.addChildMdmDropDownItems(workbook, taxonomyId, cell.field.id, item.id, item.childItems);
}

private getValidationRangeForType(workbook: Excel.Workbook, taxonomyId: number, cell: DataCell): Excel.Range {
    if (cell.field.type === FieldType.Dropdown) return this.getDropDownItemsRange(workbook, cell.field.id);
    if (cell.field.type === FieldType.MDMData) return this.getMdmDropDownItemsRange(workbook, taxonomyId, cell.field.id);
    if (cell.field.type === FieldType.Taxonomy) return this.getTaxonomyValuesRange(workbook, cell.field.taxonomyId);
    return null;
}

    setupHiddenFields(table: Excel.Table, fields: Field[]): void {
        this.logger.debug('Setting up hidden fields..');

        for (const field of fields) {
            if (!this.cache.fields.exists(field.id)) {
                this.addField(table.context.workbook, field);
            }
        }
    }

    //#endregion

    //#region Process a table

    async getRowsAsync(table: Excel.Table, excelRange: Excel.Range, taxonomyId: number): Promise<TaxonomyRow[] | null> {
        const sheet = excelRange.worksheet;
        const headerRange = table.getHeaderRowRange();
        const fieldIds = this.cache.taxonomies.getFieldIds(taxonomyId);
        const fields = fieldIds.map(id => this.cache.fields.getField(id));
        const visibleFields = fields.filter(f => f.type !== FieldType.TaxonomyId);

        sheet.load(['name']);
        headerRange.load(['rowIndex', 'columnIndex']);
        excelRange.load(['rowIndex', 'columnIndex', 'columnCount', 'rowCount']);

        await table.context.sync();

        const taxonomyStatusColumnIndex = headerRange.columnIndex + visibleFields.length + 2 /* taxonomy value + status */ - 1;
        const taxonomyValueColumnIndex = taxonomyStatusColumnIndex - 1;

        if (excelRange.rowIndex === headerRange.rowIndex ||
            excelRange.columnIndex === taxonomyStatusColumnIndex ||
            excelRange.columnIndex === taxonomyValueColumnIndex) {
            return null;
        }

        this.logger.debug('Getting taxonomy rows..');

        const range = Range.fromExcelRange(excelRange);
        const rows: TaxonomyRow[] = [];
        const dataValidations: string[][] = [];

        // Load all values in one massive chunk instead of row-by-row
        const fullDataRange = sheet
            .getRangeByIndexes(excelRange.rowIndex, headerRange.columnIndex, excelRange.rowCount, visibleFields.length + 1)
            .load(['values']);

        await table.context.sync();

        // Batch data Validation loading
        for (let batchStart = 0; batchStart < excelRange.rowCount; batchStart += ExcelService.batchSize) {
            const batchEnd = Math.min(batchStart + ExcelService.batchSize, excelRange.rowCount);
            const batchValidations: Excel.DataValidation[][] = [];

            for (let r = batchStart; r < batchEnd; r++) {
                const rowIndex = excelRange.rowIndex + r;
                const rowValidations: Excel.DataValidation[] = [];

                for (let c = 0; c < visibleFields.length + 1; c++) {
                    const columnIndex = headerRange.columnIndex + c;
                    let dataValidation: Excel.DataValidation = null;

                    if (c >= visibleFields.length || visibleFields[c].type === FieldType.MDMSearch) {
                        dataValidation = sheet
                            .getRangeByIndexes(rowIndex, columnIndex, 1, 1)
                            .dataValidation
                            .load(['prompt/message']);
                    }

                    rowValidations.push(dataValidation);
                }
                batchValidations.push(rowValidations);
            }

            await table.context.sync();

            for (let i = 0; i < batchValidations.length; i++) {
                const r = batchStart + i;
                dataValidations[r] = [];
                for (let c = 0; c < visibleFields.length + 1; c++) {
                    const dv = batchValidations[i][c];
                    // Save just the message strings to free up memory
                    dataValidations[r].push(dv && dv.prompt ? dv.prompt.message : null);
                }
            }
        }

        for (let r = 0; r < excelRange.rowCount; r++) {
            const rowIndex = excelRange.rowIndex + r;
            const message = dataValidations[r][visibleFields.length];
            const lastStatus = message ? this.appHelper.getStatus(message) : TaxonomyStatus.New;
            const taxonomyValue = lastStatus === TaxonomyStatus.Submitted
                ? fullDataRange.values[r][visibleFields.length].toString()
                : '';
            const taxonomyValueRange = new Range(rowIndex, taxonomyValueColumnIndex, 1, 1, sheet.name);
            const taxonomyStatusRange = new Range(rowIndex, taxonomyStatusColumnIndex, 1, 1, sheet.name);
            const taxonomyRow = new TaxonomyRow([], new TaxonomyValue(taxonomyValueRange, taxonomyStatusRange, lastStatus, taxonomyValue));

            for (let f = 0, c = 0; f < fieldIds.length; f++) {
                const field = fields[f];

                if (!this.appHelper.includes(visibleFields, field)) {
                    taxonomyRow.cells.push(new TaxonomyCell(undefined, undefined, field,
                        undefined, false, false, false, undefined, undefined));
                } else {
                    // NOTE: We build a cell for hidden fields as well so that cascading
                    // (applyCascading in TaxonomyService) can include them. The value
                    // generation for hidden fields is still skipped inside applyTaxonomyValue.
                    let dropDownItems: DropDownItem[];
                    let allDropDownItems: DropDownItem[];
                    let itemId: string;
                    const value = fullDataRange.values[r][c];
                    const cellRange = new Range(rowIndex, headerRange.columnIndex + c, 1, 1, sheet.name);
                    const isInRange = range.intersects(cellRange);

                    switch (field.type) {
                        case FieldType.Dropdown:
                            dropDownItems = this.cache.dropDownItems.getItems(field.id);

                            for (const item of dropDownItems) {
                                if (this.cache.childDropDownItems.exists(field.id, item.id)) {
                                    item.childItems = this.cache.childDropDownItems.getChildItems(field.id, item.id);
                                }
                            }
                            break;
                        case FieldType.MDMData:
                            dropDownItems = this.cache.mdmDropDownItems.getItems(taxonomyId, field.id);

                            for (const item of dropDownItems) {
                                if (this.cache.childMdmDropDownItems.exists(taxonomyId, field.id, item.id)) {
                                    item.childItems = this.cache.childMdmDropDownItems.getChildItems(taxonomyId, field.id, item.id);
                                }
                            }
                            break;
                        case FieldType.MDMSearch:
                            const cellMessage = dataValidations[r][c];

                            if (cellMessage && cellMessage[0] === '[' && cellMessage[cellMessage.length - 1] === ']') {
                                itemId = cellMessage.slice(1, cellMessage.length - 1);
                                dropDownItems = this.cache.mdmSearchDropDownItems.getItems(taxonomyId, field.id, itemId);
                            }
                            break;
                        case FieldType.Taxonomy:
                            dropDownItems = this.cache.taxonomyValues.getItems(field.taxonomyId);
                            break;
                    }

                    if (dropDownItems) {
                        allDropDownItems = [...dropDownItems];
                    }

                    taxonomyRow.cells.push(new TaxonomyCell(itemId,
                        cellRange, field, value, isInRange, false, false,
                        allDropDownItems, dropDownItems));

                    c++;
                }
            }

            rows.push(taxonomyRow);
        }

        return rows.filter(r => r.cells.length > 0);
    }

    async downloadDataAsync(sheet: Excel.Worksheet, rows: TaxonomyRow[], taxonomyId: number): Promise<void> {
        this.logger.debug('Downloading data..');
        let syncChanges = false;

        for (let r = 0; r < rows.length; r++) {
            const row = rows[r];

            try {
                for (const cell of row.cells) {
                    if (cell.isInRange) {
                        switch (cell.field.type) {
                            case FieldType.MDMSearch:
                                if (cell.items?.length > 0 !== true || !cell.value || !this.appHelper.find(cell.items, i => i.name == cell.value)) {
                                    const book = sheet.context.workbook;
                                    const excelRange = cell.range.toExcelRange(sheet);
                                    const newItems = await this.queryService
                                        .getMdmSearchDropDownItems(taxonomyId, cell.value, cell.field.mdmEntity, cell.field.id)
                                        .toPromise();

                                    cell.id = undefined;
                                    cell.allItems = newItems ? newItems : [];
                                    cell.items = [...newItems];

                                    if (cell.items.length > 0) {
                                        if (this.cache.mdmSearchDropDownItems.itemsExist(taxonomyId, cell.field.id, newItems)) {
                                            cell.id = this.cache.mdmSearchDropDownItems.getItemId(taxonomyId, cell.field.id, newItems);
                                        } else {
                                            cell.id = this.appHelper.getNewGuid();
                                            this.addMdmSearchDropDownItems(book, taxonomyId, cell.field.id, cell.id, newItems);
                                        }

                                        const itemsRange = this.getMdmSearchDropDownItemsRange(book, taxonomyId, cell.field.id, cell.id);

                                        this.setMdmSearchDropDownDataValidation(excelRange, itemsRange, cell.id);
                                    } else if (cell.value) {
                                        this.setMessageDataValidation(excelRange, ExcelService.noItemsFoundMessage);
                                    } else {
                                        this.setMessageDataValidation(excelRange, ExcelService.enterValueMessage);
                                    }

                                    syncChanges = true;
                                }
                                break;
                        }
                    }
                }
            } catch (error) {
                row.taxonomyValue.setStatus(TaxonomyStatus.Error, this.appHelper.getErrorMessage(error));
            }

            if (syncChanges && r !== 0 && r % ExcelService.batchSize === 0) {
                await sheet.context.sync();
                syncChanges = false;
            }
        }

        if (syncChanges) {
            await sheet.context.sync();
        }
    }

    async validateDataAsync(sheet: Excel.Worksheet, rows: TaxonomyRow[]): Promise<void> {
        this.logger.debug('Validating data..');
        let syncChanges = false;

        for (let r = 0; r < rows.length; r++) {
            const row = rows[r];

            for (const cell of row.cells) {
                if (cell.isInRange) {
                    switch (cell.field.type) {
                        case FieldType.Static:
                            if (cell.field.staticValue && cell.field.staticValue != cell.value) {
                                const exRange = cell.range.toExcelRange(sheet);

                                exRange.clear(Excel.ClearApplyTo.all);

                                this.setTextDataFormat(exRange, 1, 1);
                                this.setMessageDataValidation(exRange, ExcelService.staticValueMessage);

                                exRange.values = [[cell.value = cell.field.staticValue]];

                                syncChanges = true;
                            }
                            break;
                    }
                }
            }

            if (syncChanges && r !== 0 && r % ExcelService.batchSize === 0) {
                await sheet.context.sync();
                syncChanges = false;
            }
        }

        if (syncChanges) {
            await sheet.context.sync();
        }
    }

    async validateDropDownItemsAsync(table: Excel.Table, rows: TaxonomyRow[], taxonomyId: number): Promise<void> {
        this.logger.debug('Validating drop-down items..');
        let syncChanges = false;

        const cellsToValidate: { cell: TaxonomyCell, range: Excel.Range, dataValidation: Excel.DataValidation; }[] = [];

        for (let r = 0; r < rows.length; r++) {
            for (const cell of rows[r].cells) {
                if (cell.field.type === FieldType.Dropdown ||
                    cell.field.type === FieldType.MDMData ||
                    cell.field.type === FieldType.MDMSearch) {
                    const excelRange = cell.range.toExcelRange(table.worksheet);
                    const dataValidation = excelRange.dataValidation.load(['rule']);

                    cellsToValidate.push({
                        cell: cell,
                        range: excelRange,
                        dataValidation: dataValidation
                    });

                    syncChanges = true;
                }
            }

            if (syncChanges && r !== 0 && r % ExcelService.batchSize === 0) {
                await table.context.sync();
                syncChanges = false;
            }
        }

        if (syncChanges) {
            await table.context.sync();
            syncChanges = false;
        }

        if (cellsToValidate.length > 0) {
            const toValidate: { cell: TaxonomyCell, range: Excel.Range, itemsRange: Range, exItemsRange: Excel.Range; }[] = [];

            for (let i = 0; i < cellsToValidate.length; i++) {
                const obj = cellsToValidate[i];
                const listSource = obj.dataValidation.rule?.list?.source;
                const range = obj.range;
                const cell = obj.cell;
                const items = cell.items;

                if (!listSource || (typeof listSource === 'string' && listSource.indexOf('!#REF!') >= 0)) { // there are no drop-down items
                    if (items?.length > 0) {
                        items.splice(0, items.length);
                    }
                } else if (typeof listSource === 'string') {
                    if (listSource[0] !== '=') { // it's an inline list
                        const itemNames = listSource.split(ExcelService.defaultListItemDelimiter);
                        const allExists = itemNames.every(i => this.appHelper.findIndex(items, d => d.name == i) >= 0);

                        if (!allExists) {
                            if (cell.items?.length > 0) {
                                const fullItemsRange = cell.field.type === FieldType.Dropdown
                                    ? this.getDropDownItemsRange(table.context.workbook, cell.field.id)
                                    : this.getMdmDropDownItemsRange(table.context.workbook, taxonomyId, cell.field.id);

                                this.setDropDownDataValidation(range, fullItemsRange);

                                if (!this.appHelper.find(cell.items, i => i.name == cell.value)) {
                                    range.values = [[cell.value = '']];
                                }
                            } else {
                                this.setEmptyDropDownDataValidation(range);

                                if (cell.items.length > 0) {
                                    cell.items.splice(0, cell.items.length);
                                }

                                range.values = [[cell.value = '']];
                            }

                            syncChanges = true;
                        } else {
                            for (let i = 0; i < items.length;) {
                                const item = items[i];

                                if (itemNames.indexOf(item.name) === -1) { // the item was filtered
                                    items.splice(i, 1);
                                } else {
                                    i++;
                                }
                            }
                        }
                    } else if (cell.field.type === FieldType.MDMSearch) {
                        const itemsRange = Range.parse(listSource);

                        if (itemsRange.sheetName !== ExcelService.mdmSearchDropDownItemsSheetName ||
                            !this.mdmSearchDropDownItemsExist(taxonomyId, cell.field.id, cell.id)) {
                            range.clear(Excel.ClearApplyTo.all);
                            this.setTextDataFormat(range, 1, 1);
                            this.setMessageDataValidation(range, ExcelService.enterValueMessage);
                        }
                    } else {
                        const itemsRange = Range.parse(listSource);
                        const sheet = itemsRange.sheetName
                            ? table.context.workbook.worksheets.getItem(itemsRange.sheetName)
                            : table.context.workbook.worksheets.getActiveWorksheet();
                        const exItemsRange = itemsRange.toExcelRange(sheet);

                        if (itemsRange.sheetName === ExcelService.cascadingSheetName) { // if filtered
                            exItemsRange.load(['values', 'rowIndex']);
                        } else {
                            exItemsRange.load(['values']);
                        }

                        toValidate.push({ cell: cell, range: range, itemsRange: itemsRange, exItemsRange: exItemsRange });
                        syncChanges = true;
                    }
                }

                if (syncChanges && i !== 0 && i % ExcelService.batchSize === 0) {
                    await table.context.sync();
                    syncChanges = false;
                }
            }

            if (syncChanges) {
                await table.context.sync();
                syncChanges = false;
            }

            for (let i = 0; i < toValidate.length; i++) {
                const cellToValidate = toValidate[i];
                const cell = cellToValidate.cell;
                const range = cellToValidate.range;
                const itemsRange = cellToValidate.itemsRange;
                const exItemsRange = cellToValidate.exItemsRange;
                const items: string[] = [];

                for (let r = 0; r < exItemsRange.values.length; r++) {
                    const row = exItemsRange.values[r];

                    for (let c = 0; c < row.length; c++) {
                        items.push(row[c]);
                    }
                }

                if (itemsRange.sheetName === ExcelService.cascadingSheetName) {
                    const cascading = this.cache.cascading.getCascading(exItemsRange.rowIndex);

                    if (taxonomyId !== cascading.taxonomyId || cell.field.id !== cascading.fieldId) { // wrong range
                        if (cell.items?.length > 0) {
                            const fullItemsRange = cell.field.type === FieldType.Dropdown
                                ? this.getDropDownItemsRange(table.context.workbook, cell.field.id)
                                : this.getMdmDropDownItemsRange(table.context.workbook, taxonomyId, cell.field.id);

                            this.setDropDownDataValidation(range, fullItemsRange);
                        } else {
                            this.setEmptyDropDownDataValidation(range);
                        }

                        syncChanges = true;
                    } else {
                        for (let i = 0; i < cell.items.length;) {
                            const item = cell.items[i];

                            if (items.indexOf(item.name) === -1) { // the item was filtered
                                cell.items.splice(i, 1);
                            } else {
                                i++;
                            }
                        }
                    }
                } else {
                    const validSheetName = cell.field.type === FieldType.Dropdown
                        ? ExcelService.dropDownItemsSheetName
                        : ExcelService.mdmDropDownItemsSheetName;

                    if (itemsRange.sheetName !== validSheetName || items.length !== cell.items.length || !cell.items.every(i => this.appHelper.includes(items, i.name))) {
                        if (cell.items?.length > 0) {
                            const fullItemsRange = cell.field.type === FieldType.Dropdown
                                ? this.getDropDownItemsRange(table.context.workbook, cell.field.id)
                                : this.getMdmDropDownItemsRange(table.context.workbook, taxonomyId, cell.field.id);

                            this.setDropDownDataValidation(range, fullItemsRange);
                        } else {
                            this.setEmptyDropDownDataValidation(range);
                        }

                        syncChanges = true;
                    }
                }

                if (syncChanges && i !== 0 && i % ExcelService.batchSize === 0) {
                    await table.context.sync();
                    syncChanges = false;
                }
            }

            if (syncChanges) {
                await table.context.sync();
            }
        }
    }

    setChanges(sheet: Excel.Worksheet, taxonomyId: number, rows: TaxonomyRow[]): void {
        this.logger.debug('Applying changes..');

        rows.forEach(r => this.setTaxonomyValue(sheet, r.taxonomyValue));

        for (const row of rows) {
            for (const cell of row.cells) {
                if (cell.valueChanged || cell.itemsChanged) {
                    const workbook = sheet.context.workbook;
                    const range = sheet.getRangeByIndexes(cell.range.rowIndex, cell.range.columnIndex, 1, 1);

                    this.logger.debug(`Processing a field.. Name: '${cell.field.name}'.`);

                    if (cell.valueChanged) {
                        range.values = [[cell.value]];
                    }

                    if (cell.itemsChanged) {
                        if (cell.items.length === 0) {
                            this.setEmptyDropDownDataValidation(range);
                        } else if (cell.items.length < cell.allItems.length) { // filtered
                            const items = cell.items.map(i => i.name);
                            // const listLength = items.join(ExcelService.defaultListItemDelimiter).length;

                            // if (listLength <= ExcelService.maxDirectDropDownListLength) {
                            //     this.setInlineDropDownDataValidation(range, items);
                            // } else {
                            if (!this.cache.cascading.itemsExist(taxonomyId, cell.field.id, items)) {
                                this.addCascading(workbook, taxonomyId, cell.field.id, items);
                            }

                            const itemsRange = this.getCascadingRange(workbook, taxonomyId, cell.field.id, items);
                            this.setDropDownDataValidation(range, itemsRange);
                            //}
                        } else {
                            switch (cell.field.type) {
                                case FieldType.Dropdown:
                                    this.setDropDownDataValidation(range, this.getDropDownItemsRange(workbook, cell.field.id));
                                    break;
                                case FieldType.MDMData:
                                    this.setDropDownDataValidation(range, this.getMdmDropDownItemsRange(workbook, taxonomyId, cell.field.id));
                                    break;
                                default:
                                    throw new Error(`Unknown field type: ${cell.field.type}.`);
                            }
                        }
                    }
                }
            }
        }
    }

    //#endregion

    //#region Process a taxonomy value

    private setTaxonomyValue(sheet: Excel.Worksheet, value: TaxonomyValue): void {
        const valueRange = sheet.getRangeByIndexes(
            value.valueRange.rowIndex,
            value.valueRange.columnIndex,
            value.valueRange.rowCount,
            value.valueRange.columnCount);
        const statusRange = sheet.getRangeByIndexes(
            value.statusRange.rowIndex,
            value.statusRange.columnIndex,
            value.statusRange.rowCount,
            value.statusRange.columnCount);

        const taxonomyValue = this.getTaxonomyValueContent(value);

        this.logger.debug(`Appliying a taxonomy value.. Value: '${taxonomyValue}'. Status: '${value.status.message}'.`);

        valueRange.values = [[taxonomyValue]];
        statusRange.values = [[value.status.message]];

        this.setupTaxonomyValueStyle(valueRange, value);
        this.setupTaxonomyValueStyle(statusRange, value);
        this.setupTaxonomyValueDataValidation(valueRange, value);
    }

    private getTaxonomyValueContent(value: TaxonomyValue): string {
        switch (value.status.value) {
            case TaxonomyStatus.Empty:
                return '';

            case TaxonomyStatus.Error:
                return 'ERROR';

            case TaxonomyStatus.Warning:
                return value.value ? value.value : 'WARNING';

            default:
                return value.value;
        }
    }

    private setupTaxonomyValueStyle(range: Excel.Range, value: TaxonomyValue): void {
        switch (value.status.value) {
            case TaxonomyStatus.Submitted:
                range.style = 'good';
                break;

            case TaxonomyStatus.Error:
            case TaxonomyStatus.SubmittingError:
                range.style = 'bad';
                break;

            case TaxonomyStatus.Warning:
                range.style = 'neutral';
                break;

            case TaxonomyStatus.Empty:
            case TaxonomyStatus.New:
                range.style = 'normal';
                this.setDataAligning(range);
                break;

            default:
                throw new Error(`Unknow status: '${value.status.value}'.`);
        }
    }

    private setupTaxonomyValueDataValidation(range: Excel.Range, value: TaxonomyValue): void {
        if (value.status.value === TaxonomyStatus.Empty) {
            range.dataValidation.clear();
        } else {
            range.dataValidation.prompt = {
                showPrompt: true,
                title: 'Status',
                message: this.appHelper.getStatusString(value.status.value)
            };
        }
    }

    //#endregion

    //#region Delete a table

    async onTableRemoved(workbook: Excel.Workbook, taxonomyId: number): Promise<void> {
        const fieldIds = this.cache.taxonomies
            .getFieldIds(taxonomyId)
            .filter((id, index, arr) => arr.indexOf(id) === index); // get only unique values

        // remove fields
        for (let i = 0; i < fieldIds.length; i++) {
            const fieldId = fieldIds[i];
            const field = this.cache.fields.getField(fieldId);
            const cascadingFieldKeys = this.cache.cascading.getFieldKeys(taxonomyId, fieldId);

            if (this.cache.taxonomies.getTaxonomyIds(fieldId).length === 1) { // if it is the last taxonomy which uses the field
                switch (field.type) {
                    case FieldType.Dropdown:
                        for (const item of this.getDropDownItems(fieldId)) {
                            this.removeChildDropDownItems(workbook, fieldId, item.id);
                        }

                        this.removeDropDownItems(workbook, fieldId);
                        break;
                    case FieldType.Taxonomy:
                        if (this.cache.fields.getTaxonomyFieldIds(field.taxonomyId).length === 1) {
                            this.removeTaxonomyValues(workbook, field.taxonomyId);
                        }
                }

                this.removeField(workbook, fieldId);
            }

            if (field.type === FieldType.MDMData) {
                for (const item of this.getMdmDropDownItems(taxonomyId, fieldId)) {
                    this.removeChildMdmDropDownItems(workbook, taxonomyId, fieldId, item.id);
                }

                this.removeMdmDropDownItems(workbook, taxonomyId, fieldId);
            }

            if (field.type === FieldType.MDMSearch) {
                const itemIds = this.getMdmSearchItemIds(taxonomyId, fieldId);

                if (itemIds) {
                    for (const itemId of itemIds) {
                        this.removeMdmSearchDropDownItems(workbook, taxonomyId, fieldId, itemId);
                    }
                }
            }

            if (cascadingFieldKeys) {
                for (const fieldKey of cascadingFieldKeys) {
                    this.removeCascading(workbook, taxonomyId, fieldId, fieldKey);
                }
            }

            if (i !== 0 && i % ExcelService.batchSize === 0) {
                await workbook.context.sync();
            }
        }

        this.removeTaxonomy(workbook, taxonomyId);

        await workbook.context.sync();
    }

    //#endregion

    //#region Data formatting

    private setTextDataFormat(range: Excel.Range, rowCount: number, columnCount: number): void {
        this.setDataFormat(range, rowCount, columnCount, '@');
    }

    private setNumberDataFormat(range: Excel.Range, rowCount: number, columnCount: number): void {
        this.setDataFormat(range, rowCount, columnCount, '#,###.00');
    }

    private setDataFormat(range: Excel.Range, rowCount: number, columnCount: number, format: string): void {
        const rangeFormat = [];

        for (let r = 0; r < rowCount; r++) {
            const rowFormat = [];

            for (let c = 0; c < columnCount; c++) {
                rowFormat.push(format);
            }

            rangeFormat.push(rowFormat);
        }

        range.numberFormat = rangeFormat;
    }

    private setValue(range: Excel.Range, rowCount: number, columnCount: number, value: any): void {
        const rangeValues = [];

        for (let r = 0; r < rowCount; r++) {
            const rowValues = [];

            for (let c = 0; c < columnCount; c++) {
                rowValues.push(value);
            }

            rangeValues.push(rowValues);
        }

        range.values = rangeValues;
    }


    private setHeaderAligning(range: Excel.Range): void {
        range.format.horizontalAlignment = Excel.HorizontalAlignment.center;
        range.format.verticalAlignment = Excel.VerticalAlignment.center;
    }

    private setDataAligning(range: Excel.Range): void {
        range.format.horizontalAlignment = Excel.HorizontalAlignment.left;
        range.format.verticalAlignment = Excel.VerticalAlignment.center;
    }

    //#endregion

    //#region Data validation

    private setTextDataValidation(range: Excel.Range, maxLength: number): void {
        //range.dataValidation.clear();
        //range.dataValidation.ignoreBlanks = false;

        range.dataValidation.rule = {
            textLength: {
                formula1: 1,
                formula2: maxLength,
                operator: Excel.DataValidationOperator.between
            }
        };
        range.dataValidation.errorAlert = {
            showAlert: true,
            style: 'Stop',
            title: 'Data validation error',
            message: `This value doesn't match the data validation restrictions: only value lengths between '1' and '${maxLength}' are allowed.`,
        };
    }

    private setNumberDataValidation(range: Excel.Range): void {
        //range.dataValidation.clear();
        //range.dataValidation.ignoreBlanks = false;

        range.dataValidation.rule = {
            decimal: {
                formula1: ExcelService.numberValidationFrom,
                operator: Excel.DataValidationOperator.greaterThanOrEqualTo
            }
        };
        range.dataValidation.errorAlert = {
            showAlert: true,
            style: 'Stop',
            title: 'Data validation error',
            message: `This value doesn't match the data validation restrictions: only numbers are allowed.`,
        };
    }

    private setDateDataValidation(range: Excel.Range): void {
        //range.dataValidation.clear();
        //range.dataValidation.ignoreBlanks = false;

        range.dataValidation.rule = {
            date: {
                formula1: ExcelService.dateValidationFrom,
                formula2: ExcelService.dateValidationTo,
                operator: Excel.DataValidationOperator.between
            }
        };
        range.dataValidation.errorAlert = {
            showAlert: true,
            style: 'Stop',
            title: 'Data validation error',
            message: `This value doesn't match the data validation restrictions: only dates between '${ExcelService.dateValidationFrom}' and '${ExcelService.dateValidationTo}' are allowed.`,
        };
    }

    private setDropDownDataValidation(range: Excel.Range, itemsRange: Excel.Range, showAlert: boolean = true): void {
        range.dataValidation.clear();
        //range.dataValidation.ignoreBlanks = false;

        range.dataValidation.rule = {
            list: {
                inCellDropDown: true,
                source: itemsRange
            }
        };
        range.dataValidation.errorAlert = {
            showAlert: showAlert,
            style: 'Stop',
            title: 'Data validation error',
            message: `This value doesn't match the data validation restrictions: only values from the list are allowed.`,
        };
    }

    private setMdmSearchDropDownDataValidation(range: Excel.Range, itemsRange: Excel.Range, itemId: string): void {
        range.dataValidation.clear();
        //range.dataValidation.ignoreBlanks = false;

        range.dataValidation.rule = {
            list: {
                inCellDropDown: true,
                source: itemsRange
            }
        };
        range.dataValidation.errorAlert = {
            showAlert: false,
            style: 'Stop',
            title: 'Data validation error',
            message: `This value doesn't match the data validation restrictions: only values from the list are allowed.`,
        };
        range.dataValidation.prompt = {
            showPrompt: false,
            title: 'Data validation info',
            message: '[' + itemId + ']'
        };
    }

    private setInlineDropDownDataValidation(range: Excel.Range, items: string[], showAlert: boolean): void {
        range.dataValidation.clear();
        //range.dataValidation.ignoreBlanks = false;

        range.dataValidation.rule = {
            list: {
                inCellDropDown: true,
                source: items.join(ExcelService.defaultListItemDelimiter)
            }
        };
        range.dataValidation.errorAlert = {
            showAlert: showAlert,
            style: 'Stop',
            title: 'Data validation error',
            message: `This value doesn't match the data validation restrictions: only values from the list are allowed.`,
        };
    }

    private setEmptyDropDownDataValidation(range: Excel.Range): void {
        range.dataValidation.clear();
        //range.dataValidation.ignoreBlanks = false;

        range.dataValidation.rule = {
            textLength: {
                formula1: 1,
                operator: Excel.DataValidationOperator.lessThan
            }
        };
        range.dataValidation.errorAlert = {
            showAlert: true,
            style: 'Stop',
            title: 'Data validation error',
            message: `This value doesn't match the data validation restrictions: no values are allowed.`,
        };
        range.dataValidation.prompt = {
            showPrompt: true,
            title: 'Data validation info',
            message: ExcelService.noItemsMessage,
        };
    }

    private setMessageDataValidation(range: Excel.Range, message: string, title: string = 'Data validation info', showPrompt: boolean = true): void {
        range.dataValidation.clear();

        range.dataValidation.prompt = {
            showPrompt: showPrompt,
            title: title,
            message: message
        };
    }

    //#endregion

    //#region Taxonomies

    taxonomyExists(taxonomyId: number): boolean {
        return this.cache?.taxonomies?.exists(taxonomyId) === true;
    }

    private addTaxonomy(workbook: Excel.Workbook, taxonomyId: number, fieldIds: number[]): boolean {
        const taxonomies = this.cache.taxonomies;

        if (!taxonomies.exists(taxonomyId)) {
            this.logger.debug(`Adding a taxonomy.. Id: '${taxonomyId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.dataSheetName);
            const table = sheet.tables.getItem(ExcelService.taxonomiesTableName);
            const values = [
                taxonomyId,
                fieldIds.join(ExcelService.defaultItemDelimiter)
            ];

            const rowRange = taxonomies.containsData
                ? table.rows.add(null).getRange()
                : table.rows.getItemAt(0).getRange();

            this.setTextDataFormat(rowRange, 1, values.length);
            rowRange.values = [values];

            return taxonomies.add(taxonomyId, fieldIds);
        }

        return false;
    }

    private removeTaxonomy(workbook: Excel.Workbook, taxonomyId: number): boolean {
        const taxonomies = this.cache.taxonomies;

        if (taxonomies.exists(taxonomyId)) {
            this.logger.debug(`Removing a taxonomy.. Id: '${taxonomyId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.dataSheetName);
            const table = sheet.tables.getItem(ExcelService.taxonomiesTableName);
            const rowPosition = taxonomies.getRowPosition(taxonomyId);
            const row = table.rows.getItemAt(rowPosition);

            if (taxonomies.range.rowCount > 2 /* header + one row */) {
                row.delete();
            } else {
                row.getRange().clear(Excel.ClearApplyTo.all);
            }

            return taxonomies.remove(taxonomyId);
        }

        return false;
    }

    //#endregion

    //#region Fields

    fieldExists(fieldId: number): boolean {
        return this.cache?.fields?.exists(fieldId) === true;
    }

    private addField(workbook: Excel.Workbook, field: Field): boolean {
        const fields = this.cache.fields;

        if (!fields.exists(field.id)) {
            this.logger.debug(`Adding a field.. Id: '${field.id}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.dataSheetName);
            const table = sheet.tables.getItem(ExcelService.fieldsTableName);
            const values = [
                field.id,
                field.containsTaxonomyId ? field.taxonomyId : '',
                field.type,
                field.name,
                field.hidden ? 1 : '',
                field.mandatory ? 1 : '',
                field.type === FieldType.Text ? field.characterLimit : '',
                field.delimiter,
                field.type === FieldType.Date ? field.dateFormat : '',
                field.type === FieldType.MDMSearch ? field.mdmEntity : '',                
                field.type === FieldType.Static ? field.staticValue : '',
                field.displayCode
            ];

            const rowRange = fields.containsData
                ? table.rows.add(null).getRange()
                : table.rows.getItemAt(0).getRange();

            this.setTextDataFormat(rowRange, 1, values.length);
            rowRange.values = [values];

            return fields.add(field);
        }

        return false;
    }

    private removeField(workbook: Excel.Workbook, fieldId: number): boolean {
        const fields = this.cache.fields;

        if (fields.exists(fieldId)) {
            this.logger.debug(`Removing a field.. Id: '${fieldId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.dataSheetName);
            const table = sheet.tables.getItem(ExcelService.fieldsTableName);
            const rowPosition = fields.getRowPosition(fieldId);
            const row = table.rows.getItemAt(rowPosition);

            if (fields.range.rowCount > 2 /* header + one row */) {
                row.delete();
            } else {
                row.getRange().clear(Excel.ClearApplyTo.all);
            }

            return fields.remove(fieldId);
        }

        return false;
    }

    //#endregion

    //#region Drop-down items

    dropDownItemsExist(fieldId: number): boolean {
        return this.cache.dropDownItems.exists(fieldId);
    }

    getDropDownItems(fieldId: number): DropDownItem[] {
        this.logger.debug(`Getting drop-down items.. Id: '${fieldId}'.`);

        return this.cache.dropDownItems.getItems(fieldId);
    }

    private getDropDownItemsRange(workbook: Excel.Workbook, fieldId: number): Excel.Range | null {
        this.logger.debug(`Getting drop-down items range.. Id: '${fieldId}'.`);

        const dropDownItems = this.cache.dropDownItems;

        if (!dropDownItems.exists(fieldId)) {
            return null;
        }

        const sheet = workbook.worksheets.getItem(ExcelService.dropDownItemsSheetName);
        const range = dropDownItems.getRange(fieldId);

        return sheet.getRangeByIndexes(range.rowIndex, range.columnIndex, range.rowCount, range.columnCount);
    }

    private addDropDownItems(workbook: Excel.Workbook, fieldId: number, items: DropDownItem[]): boolean {
        const dropDownItems = this.cache.dropDownItems;

        if (!dropDownItems.exists(fieldId)) {
            this.logger.debug(`Adding drop-down items.. Id: '${fieldId}'. Count: '${items.length}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.dropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.dropDownItemsTableName);
            const tableRange = dropDownItems.range;
            const rowCount = items.length * 3 /* ids + codes + values */ + 1; /* items count */
            const rowDiff = rowCount - (tableRange.rowCount - 1 /* header */);
            let lastColumnIndex = dropDownItems.range.lastColumnIndex;

            this.addEmptyRowsToTable(table, rowDiff, tableRange.columnCount);

            if (dropDownItems.containsData) {
                table.columns.add(null, null, fieldId.toString());
                lastColumnIndex++;
            } else {
                table.columns.getItemAt(0).name = fieldId.toString();
            }

            if (items.length > 0) {
                const valuesRange = sheet.getRangeByIndexes(1, lastColumnIndex, rowCount, 1);

                this.setTextDataFormat(valuesRange, rowCount, 1);
                valuesRange.values = [[items.length.toString()]]
                    .concat(items.map(i => [i.id]))
                    .concat(items.map(i => [i.code]))
                    .concat(items.map(i => [i.name]));
            }

            return dropDownItems.add(fieldId, items);
        }

        return false;
    }

    private removeDropDownItems(workbook: Excel.Workbook, fieldId: number): boolean {
        const dropDownItems = this.cache.dropDownItems;

        if (dropDownItems.exists(fieldId)) {
            this.logger.debug(`Removing drop-down items.. Id: '${fieldId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.dropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.dropDownItemsTableName);
            const column = table.columns.getItem(fieldId.toString());

            if (dropDownItems.range.columnCount > 1) {
                column.delete();
            } else {
                column.getHeaderRowRange().values = [[ExcelService.defaultColumnName]];
                column.getDataBodyRange().clear(Excel.ClearApplyTo.all);
            }

            return dropDownItems.remove(fieldId);
        }

        return false;
    }

    //#endregion

    //#region Child drop-down items

    childDropDownItemsExist(fieldId: number, itemId: string): boolean {
        return this.cache.childDropDownItems.exists(fieldId, itemId);
    }

    getChildDropDownItems(fieldId: number, itemId: string): ChildDropDownItem[] {
        this.logger.debug(`Getting CHILD drop-down items.. Id: '${fieldId}'. Item id: '${itemId}'.`);

        return this.cache.childDropDownItems.getChildItems(fieldId, itemId);
    }

    private addChildDropDownItems(workbook: Excel.Workbook, fieldId: number, itemId: string, childItems: ChildDropDownItem[]): boolean {
        const childDropDownItems = this.cache.childDropDownItems;

        if (!childDropDownItems.exists(fieldId, itemId)) {
            this.logger.debug(`Adding CHILD drop-down items.. Id: '${fieldId}'. Item id: '${itemId}'. Count: '${childItems.length}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.childDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.childDropDownItemsTableName);
            const tableRange = childDropDownItems.range;
            const columnDiff = childItems.length - (tableRange.columnCount - 3 /* FieldId + ItemId + ItemsCount */);
            let lastRowIndex = tableRange.lastRowIndex;

            for (let i = 0; i < columnDiff; i++) {
                table.columns.add(null);
            }

            if (childDropDownItems.containsData) {
                table.rows.add(null);
                lastRowIndex++;
            }

            const values = [fieldId, itemId, childItems?.length > 0 ? childItems.length : '']
                .concat(childItems.map(i => i.fieldId + ExcelService.defaultItemDelimiter + i.id));

            const valuesRange = sheet.getRangeByIndexes(lastRowIndex, tableRange.columnIndex, 1, values.length);

            this.setTextDataFormat(valuesRange, 1, values.length);
            valuesRange.values = [values];

            return childDropDownItems.add(fieldId, itemId, childItems);
        }

        return false;
    }

    private removeChildDropDownItems(workbook: Excel.Workbook, fieldId: number, itemId: string): boolean {
        const childDropDownItems = this.cache.childDropDownItems;

        if (childDropDownItems.exists(fieldId, itemId)) {
            this.logger.debug(`Removing CHILD drop-down items.. Id: '${fieldId}'. Item id: '${itemId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.childDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.childDropDownItemsTableName);
            const rowPosition = childDropDownItems.getRowPosition(fieldId, itemId);
            const row = table.rows.getItemAt(rowPosition);

            if (childDropDownItems.range.rowCount > 2 /* header + one row */) {
                row.delete();
            } else {
                row.getRange().clear(Excel.ClearApplyTo.all);
            }

            return childDropDownItems.remove(fieldId, itemId);
        }

        return false;
    }

    //#endregion

    //#region Mdm drop-down items

    mdmDropDownItemsExist(taxonomyId: number, fieldId: number): boolean {
        return this.cache.mdmDropDownItems.exists(taxonomyId, fieldId);
    }

    getMdmDropDownItems(taxonomyId: number, fieldId: number): DropDownItem[] {
        this.logger.debug(`Getting MDM drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'.`);

        return this.cache.mdmDropDownItems.getItems(taxonomyId, fieldId);
    }

    private getMdmDropDownItemsRange(workbook: Excel.Workbook, taxonomyId: number, fieldId: number): Excel.Range | null {
        this.logger.debug(`Getting MDM drop-down items range.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'.`);

        const mdmDropDownItems = this.cache.mdmDropDownItems;

        if (!mdmDropDownItems.exists(taxonomyId, fieldId)) {
            return null;
        }

        const sheet = workbook.worksheets.getItem(ExcelService.mdmDropDownItemsSheetName);
        const range = mdmDropDownItems.getRange(taxonomyId, fieldId);

        return sheet.getRangeByIndexes(range.rowIndex, range.columnIndex, range.rowCount, range.columnCount);
    }

    private addMdmDropDownItems(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, items: DropDownItem[]): boolean {
        const mdmDropDownItems = this.cache.mdmDropDownItems;

        if (!mdmDropDownItems.exists(taxonomyId, fieldId)) {
            this.logger.debug(`Adding MDM drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Count: '${items.length}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.mdmDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.mdmDropDownItemsTableName);
            const tableRange = mdmDropDownItems.range;
            const rowCount = items.length * 3 /* ids + codes + values */ + 1; /* items count */
            const rowDiff = rowCount - (tableRange.rowCount - 1 /* header */);
            const columnName = fieldId.toString() + ExcelService.defaultItemDelimiter + taxonomyId.toString();
            let lastColumnIndex = mdmDropDownItems.range.lastColumnIndex;

            this.addEmptyRowsToTable(table, rowDiff, tableRange.columnCount);

            if (mdmDropDownItems.containsData) {
                table.columns.add(null, null, columnName);
                lastColumnIndex++;
            } else {
                table.columns.getItemAt(0).name = columnName;
            }

            if (items.length > 0) {
                const valuesRange = sheet.getRangeByIndexes(1, lastColumnIndex, rowCount, 1);

                this.setTextDataFormat(valuesRange, rowCount, 1);
                valuesRange.values = [[items.length.toString()]]
                    .concat(items.map(i => [i.id]))
                    .concat(items.map(i => [i.code]))
                    .concat(items.map(i => [i.name]));
            }

            return mdmDropDownItems.add(taxonomyId, fieldId, items);
        }

        return false;
    }

    private removeMdmDropDownItems(workbook: Excel.Workbook, taxonomyId: number, fieldId: number): boolean {
        const mdmDropDownItems = this.cache.mdmDropDownItems;

        if (mdmDropDownItems.exists(taxonomyId, fieldId)) {
            this.logger.debug(`Removing MDM drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.mdmDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.mdmDropDownItemsTableName);
            const columnName = fieldId.toString() + ExcelService.defaultItemDelimiter + taxonomyId.toString();
            const column = table.columns.getItem(columnName);

            if (mdmDropDownItems.range.columnCount > 1) {
                column.delete();
            } else {
                column.getHeaderRowRange().values = [[ExcelService.defaultColumnName]];
                column.getDataBodyRange().clear(Excel.ClearApplyTo.all);
            }

            return mdmDropDownItems.remove(taxonomyId, fieldId);
        }

        return false;
    }

    //#endregion

    //#region Child mdm drop-down items

    childMdmDropDownItemsExist(taxonomyId: number, fieldId: number, itemId: string): boolean {
        return this.cache.childMdmDropDownItems.exists(taxonomyId, fieldId, itemId);
    }

    getChildMdmDropDownItems(taxonomyId: number, fieldId: number, itemId: string): ChildDropDownItem[] {
        this.logger.debug(`Getting CHILD MDM drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Item id: '${itemId}'.`);

        return this.cache.childMdmDropDownItems.getChildItems(taxonomyId, fieldId, itemId);
    }

    private addChildMdmDropDownItems(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, itemId: string, childItems: ChildDropDownItem[]): boolean {
        const childMdmDropDownItems = this.cache.childMdmDropDownItems;

        if (!childMdmDropDownItems.exists(taxonomyId, fieldId, itemId)) {
            this.logger.debug(`Adding CHILD MDM drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Item id: '${itemId}'. Count: '${childItems.length}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.childMdmDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.childMdmDropDownItemsTableName);
            const tableRange = childMdmDropDownItems.range;
            const columnDiff = childItems.length - (tableRange.columnCount - 4 /* TaxonomyId + FieldId + ItemId + ItemsCount */);
            let lastRowIndex = tableRange.lastRowIndex;

            for (let i = 0; i < columnDiff; i++) {
                table.columns.add(null);
            }

            if (childMdmDropDownItems.containsData) {
                table.rows.add(null);
                lastRowIndex++;
            }

            const values = [taxonomyId, fieldId, itemId, childItems?.length > 0 ? childItems.length : '']
                .concat(childItems.map(i => i.fieldId + ExcelService.defaultItemDelimiter + i.id));

            const valuesRange = sheet.getRangeByIndexes(lastRowIndex, tableRange.columnIndex, 1, values.length);

            this.setTextDataFormat(valuesRange, 1, values.length);
            valuesRange.values = [values];

            return childMdmDropDownItems.add(taxonomyId, fieldId, itemId, childItems);
        }

        return false;
    }

    private removeChildMdmDropDownItems(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, itemId: string): boolean {
        const childMdmDropDownItems = this.cache.childMdmDropDownItems;

        if (childMdmDropDownItems.exists(taxonomyId, fieldId, itemId)) {
            this.logger.debug(`Removing CHILD MDM drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Item id: '${itemId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.childMdmDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.childMdmDropDownItemsTableName);
            const rowPosition = childMdmDropDownItems.getRowPosition(taxonomyId, fieldId, itemId);
            const row = table.rows.getItemAt(rowPosition);

            if (childMdmDropDownItems.range.rowCount > 2 /* header + one row */) {
                row.delete();
            } else {
                row.getRange().clear(Excel.ClearApplyTo.all);
            }

            return childMdmDropDownItems.remove(taxonomyId, fieldId, itemId);
        }

        return false;
    }

    //#endregion

    //#region Mdm search drop-down items

    private mdmSearchDropDownItemsExist(taxonomyId: number, fieldId: number, itemId: string): boolean {
        return this.cache.mdmSearchDropDownItems.exists(taxonomyId, fieldId, itemId);
    }

    private getMdmSearchDropDownItems(taxonomyId: number, fieldId: number, itemId: string): DropDownItem[] {
        this.logger.debug(`Getting MDM SEARCH drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Item id: '${itemId}'.`);

        return this.cache.mdmSearchDropDownItems.getItems(taxonomyId, fieldId, itemId);
    }

    private getMdmSearchItemIds(taxonomyId: number, fieldId: number): string[] {
        this.logger.debug(`Getting MDM SEARCH item ids.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'.`);

        return this.cache.mdmSearchDropDownItems.getFieldItemIds(taxonomyId, fieldId);
    }

    private getMdmSearchDropDownItemsRange(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, itemId: string): Excel.Range | null {
        this.logger.debug(`Getting MDM SEARCH drop-down items range.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Item id : '${itemId}'.`);

        const mdmSearchDropDownItems = this.cache.mdmSearchDropDownItems;

        if (!mdmSearchDropDownItems.exists(taxonomyId, fieldId, itemId)) {
            return null;
        }

        const sheet = workbook.worksheets.getItem(ExcelService.mdmSearchDropDownItemsSheetName);
        const range = mdmSearchDropDownItems.getRange(taxonomyId, fieldId, itemId);

        return range.toExcelRange(sheet);
    }

    private addMdmSearchDropDownItems(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, itemId: string, items: DropDownItem[]): boolean {
        const mdmSearchDropDownItems = this.cache.mdmSearchDropDownItems;

        if (!mdmSearchDropDownItems.exists(taxonomyId, fieldId, itemId)) {
            this.logger.debug(`Adding MDM SEARCH drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Item id: '${itemId}'. Count: '${items.length}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.mdmSearchDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.mdmSearchDropDownItemsTableName);
            const tableRange = mdmSearchDropDownItems.range;
            const columnDiff = items.length - (tableRange.columnCount - 4 /* TaxonomyId + FieldId + ItemId + ItemsCount */);
            let lastRowIndex = tableRange.lastRowIndex;

            for (let i = 0; i < columnDiff; i++) {
                table.columns.add(null);
            }

            if (mdmSearchDropDownItems.containsData) {
                table.rows.add(null);
                lastRowIndex++;
            }

            const values = [taxonomyId, fieldId, itemId, items?.length > 0 ? items.length : '']
                .concat(items.map(i => i.name));

            const valuesRange = sheet.getRangeByIndexes(lastRowIndex, tableRange.columnIndex, 1, values.length);

            this.setTextDataFormat(valuesRange, 1, values.length);
            valuesRange.values = [values];

            return mdmSearchDropDownItems.add(taxonomyId, fieldId, itemId, items);
        }

        return false;
    }

    private removeMdmSearchDropDownItems(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, itemId: string): boolean {
        const mdmSearchDropDownItems = this.cache.mdmSearchDropDownItems;

        if (mdmSearchDropDownItems.exists(taxonomyId, fieldId, itemId)) {
            this.logger.debug(`Removing MDM SEARCH drop-down items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Item id: '${itemId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.mdmSearchDropDownItemsSheetName);
            const table = sheet.tables.getItem(ExcelService.mdmSearchDropDownItemsTableName);
            const rowPosition = mdmSearchDropDownItems.getRowPosition(taxonomyId, fieldId, itemId);
            const row = table.rows.getItemAt(rowPosition);

            if (mdmSearchDropDownItems.range.rowCount > 2 /* header + one row */) {
                row.delete();
            } else {
                row.getRange().clear(Excel.ClearApplyTo.all);
            }

            return mdmSearchDropDownItems.remove(taxonomyId, fieldId, itemId);
        }

        return false;
    }

    //#endregion

    //#region Taxonomy values

    taxonomyValuesExist(taxonomyId: number): boolean {
        return this.cache.taxonomyValues.exists(taxonomyId);
    }

    getTaxonomyValues(taxonomyId: number): DropDownItem[] {
        this.logger.debug(`Getting taxonomy values.. Id: '${taxonomyId}'.`);

        return this.cache.taxonomyValues.getItems(taxonomyId);
    }

    private getTaxonomyValuesRange(workbook: Excel.Workbook, taxonomyId: number): Excel.Range | null {
        this.logger.debug(`Getting taxonomy values range.. Id: '${taxonomyId}'.`);

        const taxonomyValues = this.cache.taxonomyValues;

        if (!taxonomyValues.exists(taxonomyId)) {
            return null;
        }

        const sheet = workbook.worksheets.getItem(ExcelService.taxonomyValuesSheetName);
        const range = taxonomyValues.getRange(taxonomyId);

        return sheet.getRangeByIndexes(range.rowIndex, range.columnIndex, range.rowCount, range.columnCount);
    }

    private addTaxonomyValues(workbook: Excel.Workbook, taxonomyId: number, items: DropDownItem[]): boolean {
        const taxonomyValues = this.cache.taxonomyValues;

        if (!taxonomyValues.exists(taxonomyId)) {
            this.logger.debug(`Adding taxonomy values.. Id: '${taxonomyId}'. Count: '${items.length}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.taxonomyValuesSheetName);
            const table = sheet.tables.getItem(ExcelService.taxonomyValuesTableName);
            const tableRange = taxonomyValues.range;
            const rowCount = items.length * 2 /* codes + values */ + 1; /* item count row */
            const rowDiff = rowCount - (tableRange.rowCount - 1 /* header row */);
            let lastColumnIndex = taxonomyValues.range.lastColumnIndex;

            this.addEmptyRowsToTable(table, rowDiff, tableRange.columnCount);

            if (taxonomyValues.containsData) {
                table.columns.add(null, null, taxonomyId.toString());
                lastColumnIndex++;
            } else {
                table.columns.getItemAt(0).name = taxonomyId.toString();
            }

            if (items.length > 0) {
                const valuesRange = sheet.getRangeByIndexes(1, lastColumnIndex, rowCount, 1);

                this.setTextDataFormat(valuesRange, rowCount, 1);
                valuesRange.values = [[items.length.toString()]]
                    .concat(items.map(i => [i.code]))
                    .concat(items.map(i => [i.name]));
            }

            return taxonomyValues.add(taxonomyId, items);
        }

        return false;
    }

    private removeTaxonomyValues(workbook: Excel.Workbook, taxonomyId: number): boolean {
        const taxonomyValues = this.cache.taxonomyValues;

        if (taxonomyValues.exists(taxonomyId)) {
            this.logger.debug(`Removing taxonomy values.. Id: '${taxonomyId}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.taxonomyValuesSheetName);
            const table = sheet.tables.getItem(ExcelService.taxonomyValuesTableName);
            const column = table.columns.getItem(taxonomyId.toString());

            if (taxonomyValues.range.columnCount > 1) {
                column.delete();
            } else {
                column.getHeaderRowRange().values = [[ExcelService.defaultColumnName]];
                column.getDataBodyRange().clear(Excel.ClearApplyTo.all);
            }

            return taxonomyValues.remove(taxonomyId);
        }

        return false;
    }

    //#endregion

    //#region Cascading

    getCascadingFieldKeys(taxonomyId: number, fieldId: number): string[] {
        this.logger.debug(`Getting cascading keys.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'.`);

        return this.cache.cascading.getFieldKeys(taxonomyId, fieldId);
    }

    private getCascadingRange(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, items: string[]): Excel.Range | null {
        this.logger.debug(`Getting cascading range.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Count: '${items.length}'.`);

        const cascading = this.cache.cascading;

        if (!cascading.itemsExist(taxonomyId, fieldId, items)) {
            return null;
        }

        const sheet = workbook.worksheets.getItem(ExcelService.cascadingSheetName);
        const range = cascading.getRange(taxonomyId, fieldId, items);

        return sheet.getRangeByIndexes(range.rowIndex, range.columnIndex, range.rowCount, range.columnCount);
    }

    private addCascading(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, items: string[]): boolean {
        const cascading = this.cache.cascading;

        if (!cascading.itemsExist(taxonomyId, fieldId, items)) {
            this.logger.debug(`Adding cascading items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Count: '${items.length}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.cascadingSheetName);
            const table = sheet.tables.getItem(ExcelService.cascadingTableName);
            const tableRange = cascading.range;
            const columnDiff = items.length - (tableRange.columnCount - 3 /* TaxonomyId + FieldId + ItemsCount */);
            let lastRowIndex = tableRange.lastRowIndex;

            for (let i = 0; i < columnDiff; i++) {
                table.columns.add(null);
            }

            if (cascading.containsData) {
                table.rows.add(null);
                lastRowIndex++;
            }

            const values = [taxonomyId.toString(), fieldId, items.length].concat(items);
            const valuesRange = sheet.getRangeByIndexes(lastRowIndex, tableRange.columnIndex, 1, values.length);

            this.setTextDataFormat(valuesRange, 1, values.length);
            valuesRange.values = [values];

            return cascading.add(taxonomyId, fieldId, items);
        }

        return false;
    }

    private removeCascading(workbook: Excel.Workbook, taxonomyId: number, fieldId: number, key: string): boolean {
        const cascading = this.cache.cascading;

        if (cascading.keyExists(taxonomyId, fieldId, key)) {
            this.logger.debug(`Removing cascading items.. Id: '${fieldId}'. Taxonomy id: '${taxonomyId}'. Key: '${key}'.`);

            const sheet = workbook.worksheets.getItem(ExcelService.cascadingSheetName);
            const table = sheet.tables.getItem(ExcelService.cascadingTableName);
            const rowPosition = cascading.getRowPosition(taxonomyId, fieldId, key);
            const row = table.rows.getItemAt(rowPosition);

            if (cascading.range.rowCount > 2 /* header + one row */) {
                row.delete();
            } else {
                row.getRange().clear(Excel.ClearApplyTo.all);
            }

            return cascading.remove(taxonomyId, fieldId, key);
        }

        return false;
    }

    //#endregion

    //#region Helpers

    private addSystemTable(sheet: Excel.Worksheet, name: string, address: string, headers: string[]): Excel.Table {
        this.logger.debug(`Adding system table.. Name: '${name}'.`);

        const table = sheet.tables.add(address, true);
        table.name = name;
        table.showFilterButton = false;

        const headersRange = table.getHeaderRowRange();
        const dataRange = table.getDataBodyRange();

        headersRange.values = [headers];

        this.setHeaderAligning(headersRange);
        this.setDataAligning(dataRange);

        return table;
    }

    private addSystemSheet(workbook: Excel.Workbook, sheetName: string): Excel.Worksheet {
        this.logger.debug(`Adding system sheet.. Name: '${sheetName}'.`);

        const sheet = workbook.worksheets.add(sheetName);
        sheet.visibility = Excel.SheetVisibility.hidden;

        return sheet;
    }

    private addEmptyRowsToTable(table: Excel.Table, rowDiff: number, columnCount: number): void {
        if (rowDiff > 0) {
            const emptyRows = [];
            const emptyRow = new Array(columnCount).fill('');
            for (let i = 0; i < rowDiff; i++) {
                emptyRows.push(emptyRow);
            }
            table.rows.add(null, emptyRows);
        }
    }

    //#endregion
}
